/**
 * ENG-206 — deselect an OI once it ends, so the OI tab falls back to the
 * full list instead of holding a stale (or unrenderable) detail pane.
 *
 * Two triggers:
 *   - the selected OI's status turns Ended (rtm_state 5). Only the
 *     transition counts: an OI that was already Ended when the user opened
 *     it (Ended chip on) stays open.
 *   - the selected id no longer resolves in any source — the drone was
 *     evicted (120 s heartbeat), re-declared under a new oi_id, or a DSS
 *     result dropped out. OIDetail has nothing to render in that case; in
 *     live mode it used to show three pulsing skeletons forever.
 */
import { getCurrentExternalOis } from '@/lib/api/queries';
import type { DssOi, OiSummary } from '@/lib/api/types';
import { queryClient } from '@/lib/query';
import { type DroneLive, useDronesStore } from '@/lib/store/drones';
import { useOiStore } from '@/lib/store/oi';
import { composeOis } from './mergeOis';
import type { RtmStatus } from './rtmStatus';

/** `status` is null for a 3rd-party (DSS) OI, which carries no rtm_state. */
export type OiPresence = { found: false } | { found: true; status: RtmStatus | null };

export type OiSources = {
  restList: readonly OiSummary[];
  drones: readonly DroneLive[];
  external: readonly DssOi[];
};

/** Same precedence as the OI surfaces: REST record, then the drone's live
 *  envelope, then the DSS search results. */
export function locateOi(id: string, sources: OiSources): OiPresence {
  const rest = sources.restList.find((o) => o.oi_id === id);
  if (rest) return { found: true, status: rest.status };
  const droneOi = sources.drones.find((d) => d.oi?.oi_id === id)?.oi;
  if (droneOi) return { found: true, status: droneOi.status };
  if (sources.external.some((o) => o.oi_id === id)) return { found: true, status: null };
  return { found: false };
}

/** `prev` is null on the first look after a selection. */
export function shouldDeselect(prev: OiPresence | null, next: OiPresence): boolean {
  if (!next.found) return true;
  return next.status === 'Ended' && prev?.found === true && prev.status !== 'Ended';
}

function currentSources(): OiSources {
  // The composed list (REST + Blender + MQTT) is exactly what the OI panel
  // shows. A Scheduled OI (ENG-313) exists only in Blender, so locating it
  // anywhere narrower would bounce the user out of its detail pane at once.
  const composed = composeOis({
    rest: queryClient.getQueryData<OiSummary[]>(['oi', 'list']) ?? [],
    blender: queryClient.getQueryData<OiSummary[]>(['oi', 'blender']),
    drones: Object.values(useDronesStore.getState().byId),
    now: Date.now(),
  });
  return { restList: composed, drones: [], external: getCurrentExternalOis() };
}

export function subscribeOiAutoDeselect(): () => void {
  let prevId: string | null = null;
  let prev: OiPresence | null = null;

  const check = () => {
    const id = useOiStore.getState().selectedId;
    if (id !== prevId) {
      prevId = id;
      prev = null;
    }
    if (!id) return;
    const next = locateOi(id, currentSources());
    if (shouldDeselect(prev, next)) {
      useOiStore.getState().select(null);
      return;
    }
    prev = next;
  };

  const unsubSel = useOiStore.subscribe((s) => s.selectedId, check);
  // The DSS source is scoped to the 3rd Party category.
  const unsubCat = useOiStore.subscribe((s) => s.category, check);
  const unsubDrones = useDronesStore.subscribe((s) => s.byId, check);
  const unsubQ = queryClient.getQueryCache().subscribe((event) => {
    if (event.type === 'updated' && event.query.queryKey[0] === 'oi') check();
  });
  check();

  return () => {
    unsubSel();
    unsubCat();
    unsubDrones();
    unsubQ();
  };
}
